import Vue from 'vue'
export const TIMEZONES = [
  { label: 'Universal Coordinated Time (UTC)', value: 'UTC' },
  { label: 'Eastern Time (Washington, DC)', value: 'America/New_York' },
  { label: 'Central Time (Chicago)', value: 'America/Chicago' },
  { label: 'Mountain Time (Salt Lake City)', value: 'America/Denver' },
  { label: 'Arizona Time (Phoenix)', value: 'America/Phoenix' },
  { label: 'Pacific Time (Los Angeles)', value: 'America/Los_Angeles' },
  { label: 'Alaskan Time (Anchorage)', value: 'America/Anchorage' },
  { label: 'Hawaii-Aleutian Time (Honolulu)', value: 'Pacific/Honolulu' }
]

function snAdminCanAccess (toRoute, canAccessSupportTools) {
  const viewAsCompanyGuid = sessionStorage.getItem('evault_view_as_company_guid')
  switch (toRoute) {
    case 'Page Not Found':
    case 'MersTools':
    case 'Companies':
    case 'Company Details':
    case 'API Integrations':
    case 'Data Dashboard':
    case 'Report Details':
      return true
    case 'SupportTools':
      return canAccessSupportTools
    default:
      return viewAsCompanyGuid != null
  }
}

export default {
  formatInitiatorOrg (transaction) {
    if (transaction.initiatorRemoteMersOrgId) {
      if (transaction.initiatorName) {
        return `${transaction.initiatorName} (${transaction.initiatorRemoteMersOrgId})`
      } else {
        return transaction.initiatorRemoteMersOrgId
      }
    }
    return ''
  },

  getPartnerName (identifier, name, currentOrg) {
    let partnerName = name
    if (identifier === name) {
      partnerName = ''
    }
    if (identifier === currentOrg) {
      if (partnerName !== '') {
        partnerName += ' '
      }
      partnerName += '(me)'
    }
    return partnerName
  },

  getOrganization (identifier, name) {
    if (identifier == null || identifier === '') { // DFT can be set as an empty string via Evault Setting 9 (delegatee_org_id) - see INV-1789
      return null
    }
    if (name == null) {
      return identifier
    }
    if (name === identifier) {
      return identifier
    }
    return identifier + ' ' + name
  },

  getDataGroup (label, value) {
    if (value == null || value === '') {
      return null
    }
    return {
      label: label,
      value: value
    }
  },

  addDataGroup (list, label, value) {
    const dataGroup = this.getDataGroup(label, value)
    if (dataGroup != null) {
      list.push(dataGroup)
    }
  },

  formatDate (date) {
    const today = Vue.prototype.$moment(new Date()).format('MMM DD, YYYY')
    const calcTomorrow = new Date()
    calcTomorrow.setDate(calcTomorrow.getDate() + 1)
    const tomorrow = Vue.prototype.$moment(calcTomorrow).format('MMM DD, YYYY')

    const isToday = date === today
    const isTomorrow = date === tomorrow
    let label = ''

    if (isToday) {
      label = `Today, ${date}`
    } else if (isTomorrow) {
      label = `Tomorrow, ${date}`
    } else {
      label = date
    }
    return { label: label, isToday: isToday, isTomorrow: isTomorrow }
  },

  formatDateTimezone (timestamp, timezone) {
    const tzLabel = TIMEZONES.find(tz => tz.label === timezone)
    const tz = tzLabel ? tzLabel.value : TIMEZONES[0].value // default to UTC
    const datetime = new Date(timestamp).toLocaleString('en-US', {
      timeZone: tz,
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    })
    return datetime.replace(/^(.+?,.+?),\s*/g, '$1 ') // removes commas after first instance
  },

  formatTimestamp (timestamp, timezone) {
    const tzLabel = TIMEZONES.find(tz => tz.label === timezone)
    const tz = tzLabel ? tzLabel.value : TIMEZONES[0].value // default to UTC
    const datetime = new Date(timestamp).toLocaleString('en-US', {
      timeZone: tz,
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
    })
    return datetime.replace(/^(.+?,.+?),\s*/g, '$1 ') // removes commas after first instance
  },

  formatNumberWithCommas (number) {
    return number.toLocaleString()
  },

  toggleVerticalScrollbar (hideVerticalScrollbar) { // to fix double scrollbar when in captive flow (INV-1610)
    document.getElementsByTagName('html')[0].style.overflowY = hideVerticalScrollbar ? 'hidden' : ''
  },

  validateEmail (email) {
    return email
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      )
  },

  validateMin (min) {
    return min.match(/^\d{18}$/)
  },

  processDocumentData (base64String) {
    // Decode base64 string
    const decodedData = window.atob(base64String)

    // Create uInt8Array of size same as row data length
    const uInt8Array = new Uint8Array(decodedData.length)

    // Insert all character code into uInt8Array
    for (let i = 0; i < decodedData.length; i++) {
      uInt8Array[i] = decodedData.charCodeAt(i)
    }

    return uInt8Array
  },

  hasPermission (user, action) {
    if (user == null || !user.permissions) {
      return false
    }
    return user.isSnAdmin || !!user.permissions[action]
  },

  hasSupportToolsPermission (user) {
    if (user == null || !user.permissions) {
      return false
    }
    return user.isSnAdmin && !!user.permissions.supportToolsEnabled
  },

  hasFeatureFlag (user, featureFlag) {
    if (user == null || !user.featureFlags) {
      return false
    }
    return !!user.featureFlags[featureFlag]
  },

  getDefaultPage (user, hasMersOrgId) {
    if (user.isSnAdmin) {
      return '/companies'
    } else if (user.locked || user.companyLocked) {
      return `/no_access/${user.companyLocked ? 'company_locked' : 'user_locked'}`
    } else if (hasMersOrgId && (user.permissions.transfers ||
      user.permissions.deliveries ||
      user.permissions.changeStatus ||
      user.permissions.changeData ||
      user.permissions.register ||
      user.permissions.inquiry ||
      user.permissions.connectivity)) {
      return '/transactions'
    } else if (user.permissions.electronicRecordsEnabled) {
      return '/electronic_records'
    } else if (user.permissions.managePartners ||
      user.permissions.manageUsers ||
      user.permissions.manageRoles ||
      user.permissions.manageOrganizations ||
      user.permissions.viewAboutTab) {
      return '/company_settings'
    }
    return '/no_access/no_permissions'
  },

  requiresOrg (toRoute) {
    return ['TransactionDetails', 'Enote', 'Report Details', 'Doc Details'].includes(toRoute)
  },

  canAccess (user, toRoute, hasMersOrgId) {
    const hasPermissions = (permissions) =>
      permissions.some(permission => user.permissions[permission])

    const mayAccessEnoteTransactions = () =>
      hasMersOrgId && hasPermissions([
        'transfers',
        'deliveries',
        'changeStatus',
        'changeData',
        'register'
      ])

    const mayAccessAnyTransactions = () =>
      mayAccessEnoteTransactions() ||
      (hasMersOrgId && hasPermissions(['inquiry', 'connectivity']))

    if (user.isSnAdmin) {
      return snAdminCanAccess(toRoute, user.permissions.supportToolsEnabled)
    }
    switch (toRoute) {
      case 'Login':
      case 'Page Not Found':
      case 'MersTools':
        return true
      case 'Companies':
      case 'Company Details':
      case 'API Integrations':
        return false // only snAdmins can access these pages which is handled outside the switch statement
      case 'Transactions':
      case 'TransactionDetails':
        return mayAccessAnyTransactions(user)
      case 'EnoteInventory':
      case 'Enote':
        return mayAccessEnoteTransactions(user)
      case 'Electronic Records':
      case 'Doc Details':
        return user.permissions.electronicRecordsEnabled
      case 'Settings':
        return user.permissions.managePartners ||
          user.permissions.manageUsers ||
          user.permissions.manageRoles ||
          user.permissions.manageOrganizations
      case 'Reports':
      case 'Report Details':
        return user.permissions.manageReports
      case 'No Access':
      case 'Consent':
        return user.loggedIn
      default:
        return false
    }
  },

  formattedMassEvaultUsers (evaultUsers, compareGuids, roleGuid = null, orgGuid = null) {
    const users = []
    evaultUsers.map(user => {
      users.push({ key: user.guid, value: `${user.name} - ${user.email}`, disabled: compareGuids.includes(user.guid) || user.roleGuid === roleGuid || user.orgGuids?.includes(orgGuid) })
    })
    return users
  },

  formattedMassOrgs (orgs, compareGuids) {
    const formattedOrgs = []
    orgs.map(org => {
      formattedOrgs.push({ key: org.guid, value: `${org.remoteId} - ${org.name}`, disabled: compareGuids.includes(org.guid) })
    })
    return formattedOrgs
  },

  validSsnCheck (event) {
    let val = event.target.value.replace(/\D/g, '')
    let newVal = ''
    if (val.length > 4) {
      this.value = val
    }
    if ((val.length > 3) && (val.length < 6)) {
      newVal += val.substr(0, 3) + '-'
      val = val.substr(3)
    }
    if (val.length > 5) {
      newVal += val.substr(0, 3) + '-'
      newVal += val.substr(3, 2) + '-'
      val = val.substr(5)
    }
    newVal += val
    return newVal.substring(0, 11)
  },

  checkForSelfReload (evaultUserGuids, loggedInUser) {
    let reload = false
    evaultUserGuids.forEach(evaultUserGuid => {
      if (evaultUserGuid === loggedInUser.guid) {
        setTimeout(() => {
          window.location.reload()
        }, 3000) // delay 3 seconds with toast acknowledging application of change
        reload = true
      }
    })
    return reload
  }
}
